import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar, Clock, Plus, X, Edit2, Trash2, Search,
  TriangleAlert, CheckCircle, Grid3X3, List, Loader2, FileDown, Table2, 
  MapPin, Users, ChevronDown, BookOpen, GraduationCap
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";

// ==================== TYPES ====================
interface TimetableSlot {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject_id: number;
  subject_name?: string;
  subject_code?: string;
  teacher_id: number;
  teacher_name?: string;
  class_id: number;
  class_name?: string;
  classroom_id?: number;
  classroom_name?: string;
  slot_type?: string;
  notes?: string;
  school_id?: number;
  academic_year_id?: number;
}

interface Subject {
  id: number;
  name: string;
  code?: string;
  section: "FR" | "EN";
  coefficient: number;
  label_fr?: string;
  label_en?: string;
  section_id?: number;
  specialty_id?: number;
  status?: string;
  is_active?: boolean;
  school_id?: number;
  academic_year_id?: number;
}

interface Teacher {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  school_id?: number;
}

interface ClassOption {
  id: number;
  name: string;
  abbreviation: string;
  level_id: number;
  level_name?: string;
  section_id?: number;
  section_name?: string;
  school_id?: number;
  academic_year_id?: number;
}

interface School {
  id: number;
  name: string;
  acronym: string;
  status: string;
}

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];

// ─── Horaires calculés depuis les paramètres de génération (backend) ───
// Valeurs par défaut = horaires officiels du Lycée Bilingue de Baleng
interface GenerationSettings {
  start_time: string;
  end_time: string;
  break_count: number;
  break_duration_minutes: number;
  slot_duration_minutes: number;
  days: number[];
}

const DEFAULT_GEN_SETTINGS: GenerationSettings = {
  start_time: "08:00",
  end_time: "17:40",
  break_count: 1,
  break_duration_minutes: 30,
  slot_duration_minutes: 50,
  days: [1, 2, 3, 4, 5],
};

const FALLBACK_TIME_ROWS = ["08:00", "08:50", "09:40", "10:30", "11:20", "11:50", "12:40", "13:30", "14:20", "15:10", "16:00", "16:50"];

const toMin = (t: string): number => {
  const [h, m] = (t || "0:0").split(":").map(Number);
  return h * 60 + (m || 0);
};

const toHHMM = (mins: number): string =>
  `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

/**
 * Génère les lignes de la grille (heures de début de chaque créneau)
 * en reproduisant exactement la logique du backend (_generate_single_class) :
 * la pause tombe à la position floor(i * slots_per_day / (break_count + 1)).
 */
function computeTimeRows(s: GenerationSettings): string[] {
  const start = toMin(s.start_time);
  const end = toMin(s.end_time);
  const dur = s.slot_duration_minutes || 50;
  const breakCount = s.break_count || 0;
  const breakDur = s.break_duration_minutes || 0;
  const total = end - start - breakCount * breakDur;
  if (total <= 0 || dur <= 0) return FALLBACK_TIME_ROWS;
  const perDay = Math.floor(total / dur);
  if (perDay <= 0) return FALLBACK_TIME_ROWS;

  const breakPositions: number[] = [];
  if (breakCount > 0) {
    const step = perDay / (breakCount + 1);
    for (let i = 1; i <= breakCount; i++) breakPositions.push(Math.floor(i * step));
  }

  const rows: string[] = [];
  let t = start;
  let n = 0;
  while (n < perDay) {
    if (breakPositions.includes(n)) t += breakDur;
    rows.push(toHHMM(t));
    t += dur;
    n++;
  }
  return rows;
}

interface DisplayRow {
  label: string;
  isBreak: boolean;
  slotIndex: number; // index dans timeRows, -1 pour une pause
}

/** Lignes d'affichage = créneaux + lignes de pause entre les créneaux */
function computeDisplayRows(s: GenerationSettings): DisplayRow[] {
  const rows = computeTimeRows(s);
  const dur = s.slot_duration_minutes || 50;
  const out: DisplayRow[] = [];
  rows.forEach((label, i) => {
    const m = toMin(label);
    if (i > 0) {
      const prevM = toMin(rows[i - 1]);
      if (m - prevM > dur) {
        out.push({ label: `${toHHMM(prevM + dur)} – ${toHHMM(m)}`, isBreak: true, slotIndex: -1 });
      }
    }
    out.push({ label, isBreak: false, slotIndex: i });
  });
  return out;
}

const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id';
const SELECTED_YEAR_EVENT = 'lybibal-year-change';

const SUBJECT_COLORS_PDF: Record<string, { bg: string; border: string; text: string }> = {
  "Mathématiques": { bg: "#dbeafe", border: "#3b82f6", text: "#1e3a8a" },
  "Physique": { bg: "#e9d5ff", border: "#a855f7", text: "#6b21a8" },
  "Chimie": { bg: "#dcfce7", border: "#22c55e", text: "#15803d" },
  "Biologie": { bg: "#d1fae5", border: "#10b981", text: "#047857" },
  "Français": { bg: "#fee2e2", border: "#ef4444", text: "#b91c1c" },
  "Anglais": { bg: "#fef3c7", border: "#f59e0b", text: "#b45309" },
  "Histoire": { bg: "#ffedd5", border: "#f97316", text: "#c2410c" },
  "Géographie": { bg: "#cffafe", border: "#06b6d4", text: "#0e7490" },
  "Philosophie": { bg: "#fce7f3", border: "#ec4899", text: "#be185d" },
  "EPS": { bg: "#ecfccb", border: "#84cc16", text: "#4d7c0f" },
  "Informatique": { bg: "#e0e7ff", border: "#6366f1", text: "#4338ca" },
  "SVT": { bg: "#d1fae5", border: "#10b981", text: "#047857" },
  "Latin": { bg: "#f3e8ff", border: "#8b5cf6", text: "#6d28d9" },
  "Allemand": { bg: "#fef9c3", border: "#eab308", text: "#a16207" },
  "Espagnol": { bg: "#fecaca", border: "#f87171", text: "#dc2626" },
  "Musique": { bg: "#fce7f3", border: "#f472b6", text: "#db2777" },
  "Arts Plastiques": { bg: "#ddd6fe", border: "#8b5cf6", text: "#6d28d9" },
  "Éducation Civique": { bg: "#bfdbfe", border: "#60a5fa", text: "#2563eb" },
};

function getSubjectColorPDF(subjectName: string): { bg: string; border: string; text: string } {
  return SUBJECT_COLORS_PDF[subjectName] || { bg: "#f3f4f6", border: "#9ca3af", text: "#4b5563" };
}

function generateTimetableHTML(
  slots: TimetableSlot[], classes: ClassOption[], teachers: Teacher[], subjects: Subject[],
  filterType: "class" | "teacher" | "subject", filterId: string, title: string,
  displayRows: DisplayRow[]
): string {
  const filteredSlots = slots.filter((s) => {
    if (!filterId) return true;
    if (filterType === "class") return s.class_id?.toString() === filterId;
    if (filterType === "teacher") return s.teacher_id?.toString() === filterId;
    if (filterType === "subject") return s.subject_id?.toString() === filterId;
    return true;
  });

  const filterName =
    filterType === "class"
      ? classes.find((c) => c.id.toString() === filterId)?.name || "Toutes les classes"
      : filterType === "teacher"
      ? teachers.find((t) => t.id.toString() === filterId)?.full_name || "Tous les enseignants"
      : subjects.find((s) => s.id.toString() === filterId)?.name || "Toutes les matières";

  // ─── Bloc informations enseignant (visible uniquement sur l'impression enseignant) ───
  let teacherInfoHtml = "";
  if (filterType === "teacher" && filterId) {
    const teacher = teachers.find((t) => t.id.toString() === filterId) as any;
    if (teacher) {
      const diplome =
        teacher.diploma ||
        (Array.isArray(teacher.qualifications) ? teacher.qualifications.join(", ") : teacher.qualifications) ||
        teacher.specialty || "—";
      const contact = [teacher.phone, teacher.email].filter(Boolean).join("  |  ") || "—";
      const matieres =
        Array.isArray(teacher.subjects_specialization) && teacher.subjects_specialization.length > 0
          ? teacher.subjects_specialization.join(", ")
          : (Array.isArray(teacher.specialties) && teacher.specialties.length > 0
              ? teacher.specialties.join(", ")
              : "—");
      const classesTenues = Array.from(
        new Set(filteredSlots.map((s) => s.class_name).filter(Boolean))
      ).join(", ") || "—";
      const matricule = teacher.matricule || "";
      teacherInfoHtml = `
    <div class="teacher-info">
      <div class="ti-row"><span class="ti-label">ENSEIGNANT(E) :</span> <strong>${teacher.full_name || `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim()}</strong>${matricule ? ` <span class="ti-mat">(${matricule})</span>` : ""}</div>
      <div class="ti-grid">
        <div><span class="ti-label">GRADE/DIPLÔME :</span> ${diplome}</div>
        <div><span class="ti-label">CONTACT :</span> ${contact}</div>
        <div><span class="ti-label">MATIÈRES :</span> ${matieres}</div>
        <div><span class="ti-label">CLASSES TENUES :</span> ${classesTenues}</div>
      </div>
    </div>`;
    }
  }

  let html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title>
    <style>
      /* ═══ Impression : lisible ET compacte (≤ 2 pages) ═══ */
      @page { size: A4 landscape; margin: 0.7cm; }
      * { box-sizing: border-box; }
      body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 6px; color: #333; font-size: 10px; }
      .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; padding-bottom: 5px; border-bottom: 2px solid #1e3a8a; }
      .logo-section { display: flex; align-items: center; gap: 8px; }
      .logo-icon { width: 40px; height: 40px; background: linear-gradient(135deg, #1e3a8a, #3b82f6); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: bold; }
      .school-info h1 { margin: 0; font-size: 14px; color: #1e3a8a; font-weight: 700; }
      .school-info p { margin: 1px 0; font-size: 8.5px; color: #666; }
      .doc-title { text-align: right; }
      .doc-title h2 { margin: 0; font-size: 13px; color: #1e3a8a; }
      .doc-title p { margin: 2px 0; font-size: 8.5px; color: #888; }
      .info-box { background: #f8fafc; border-left: 3px solid #1e3a8a; padding: 4px 8px; margin-bottom: 6px; border-radius: 0 6px 6px 0; }
      .info-box p { margin: 1px 0; font-size: 9px; color: #444; }
      .info-box strong { color: #1e3a8a; }
      .teacher-info { background: #eff6ff; border: 1px solid #bfdbfe; border-left: 3px solid #2563eb; padding: 5px 10px; margin-bottom: 6px; border-radius: 0 6px 6px 0; }
      .teacher-info .ti-row { font-size: 11px; margin-bottom: 3px; color: #1e3a8a; }
      .teacher-info .ti-mat { font-size: 9px; color: #64748b; font-weight: 400; }
      .teacher-info .ti-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; }
      .teacher-info .ti-grid > div { font-size: 9px; color: #334155; }
      .teacher-info .ti-label { font-weight: 700; color: #1e3a8a; }
      /* Table : en-tête répété sur la 2ᵉ page, lignes non coupées, hauteur naturelle */
      table { width: 100%; border-collapse: collapse; margin-top: 5px; }
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      th { background: linear-gradient(135deg, #1e3a8a, #2563eb); color: white; padding: 5px 6px; font-size: 9.5px; text-align: center; border: 1px solid #1e3a8a; font-weight: 600; }
      td { border: 1px solid #e5e7eb; padding: 3px; font-size: 8.5px; vertical-align: top; width: 16%; }
      .time-col { background: #f3f4f6; font-weight: bold; text-align: center; width: 44px; font-size: 8.5px; color: #374151; }
      .slot { border-radius: 4px; padding: 3px 4px; margin: 2px 0; font-size: 8.5px; border-left: 2px solid; box-shadow: 0 1px 2px rgba(0,0,0,0.05); page-break-inside: avoid; line-height: 1.25; }
      .slot-subject { font-weight: 700; font-size: 8.5px; margin-bottom: 1px; }
      .slot-teacher { font-size: 8px; opacity: 0.9; }
      .slot-room { font-size: 8px; opacity: 0.75; }
      .slot-class { font-size: 8px; opacity: 0.75; font-style: italic; }
      .footer { margin-top: 6px; padding-top: 4px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 8.5px; color: #9ca3af; }
      .footer-signature { margin-top: 8px; display: flex; justify-content: space-between; font-size: 9px; color: #555; }
      .signature-line { margin-top: 20px; border-top: 1px solid #333; width: 180px; text-align: center; padding-top: 3px; }
    </style></head><body>
    <div class="header"><div class="logo-section"><div class="logo-icon">LB</div><div class="school-info">
      <h1>LYCÉE BILINGUE DE BALENG</h1><p>Ministère des Enseignements Secondaires - République du Cameroun</p>
      <p>BP: 1234 Bafoussam | Tel: (+237) 233 45 67 89</p></div></div><div class="doc-title">
      <h2>📅 EMPLOI DU TEMPS</h2><p>Année Scolaire 2025-2026</p>
      <p>Semaine du ${new Date().toLocaleDateString("fr-FR", { day: 'numeric', month: 'long', year: 'numeric' })}</p></div></div>
    <div class="info-box"><p><strong>Filtre appliqué:</strong> ${filterType === "class" ? "Classe" : filterType === "teacher" ? "Enseignant" : "Matière"} = ${filterName}</p>
      <p><strong>Nombre de créneaux:</strong> ${filteredSlots.length} | <strong>Généré le:</strong> ${new Date().toLocaleDateString("fr-FR", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p></div>
    ${teacherInfoHtml}
    <table><thead><tr><th>Heure</th>${DAYS.map((d) => `<th>${d}</th>`).join("")}</tr></thead><tbody>`;

  const slotRowsOnly = displayRows.filter((r) => !r.isBreak);
  displayRows.forEach((row) => {
    if (row.isBreak) {
      html += `<tr><td class="time-col" style="background:#fef3c7;color:#b45309;">☕</td>` +
        `<td colspan="${DAYS.length}" style="background:#fef9c3;text-align:center;font-weight:700;color:#b45309;border:1px solid #fde68a;font-size:10px;letter-spacing:1px;">PAUSE ${row.label}</td></tr>`;
      return;
    }
    const slotIdx = slotRowsOnly.indexOf(row);
    const rowStart = toMin(row.label);
    const rowEnd = slotIdx + 1 < slotRowsOnly.length ? toMin(slotRowsOnly[slotIdx + 1].label) : Number.MAX_SAFE_INTEGER;
    const timeSlot = row.label;
    html += `<tr><td class="time-col">${timeSlot}</td>`;
    DAYS.forEach((_, dayIdx) => {
      const daySlots = filteredSlots.filter((s) => {
        if (s.day_of_week !== dayIdx + 1) return false;
        const m = toMin((s.start_time || "00:00").slice(0, 5));
        return m >= rowStart && m < rowEnd;
      });
      if (daySlots.length === 0) {
        html += `<td class="empty">—</td>`;
      } else {
        html += `<td>`;
        daySlots.forEach((slot) => {
          const color = getSubjectColorPDF(slot.subject_name || "");
          html += `<div class="slot" style="background-color: ${color.bg}; border-color: ${color.border}; color: ${color.text};">
            <div class="slot-subject">${slot.subject_name || "Matière"}</div>
            <div class="slot-teacher">👤 ${slot.teacher_name || "Prof."}</div>
            <div class="slot-class">📚 ${slot.class_name || "Classe non définie"}</div>
            <div class="slot-room">🚪 ${slot.classroom_name || "Salle non attribuée"}</div>
            <div class="slot-time">${slot.start_time?.slice(0, 5)} - ${slot.end_time?.slice(0, 5)}</div></div>`;
        });
        html += `</td>`;
      }
    });
    html += `</tr>`;
  });

  const usedSubjects = [...new Set(filteredSlots.map(s => s.subject_name).filter((s): s is string => typeof s === "string" && s.length > 0))];
  const legendHTML = usedSubjects.map((subj: string) => {
    const c = getSubjectColorPDF(subj);
    return `<span class="legend-item"><span class="legend-color" style="background:${c.bg};border-color:${c.border}"></span>${subj}</span>`;
  }).join("");

  html += `</tbody></table><div class="legend"><div class="legend-title">📚 Légende des matières</div>${legendHTML}</div>
    <div class="footer"><div class="footer-left"><p>Document généré par le Système de Gestion Scolaire</p>
      <p>Lycée Bilingue de Baleng © ${new Date().getFullYear()}</p><p>Ce document est confidentiel et destiné à un usage interne.</p></div>
    <div class="signature-section"><div class="signature-line"></div><div class="signature-label">Signature du Proviseur</div>
      <div class="stamp">VISA PROVISEUR</div></div></div></body></html>`;

  return html;
}

function exportToPDF(slots: TimetableSlot[], classes: ClassOption[], teachers: Teacher[], subjects: Subject[], filterType: "class" | "teacher" | "subject", filterId: string, displayRows: DisplayRow[]) {
  const filterName = filterType === "class" ? classes.find((c) => c.id.toString() === filterId)?.name || "Toutes les classes" : filterType === "teacher" ? teachers.find((t) => t.id.toString() === filterId)?.full_name || "Tous les enseignants" : subjects.find((s) => s.id.toString() === filterId)?.name || "Toutes les matières";
  const title = `Emploi du temps - ${filterName}`;
  const html = generateTimetableHTML(slots, classes, teachers, subjects, filterType, filterId, title, displayRows);
  const printWindow = window.open("", "_blank");
  if (printWindow) { printWindow.document.write(html); printWindow.document.close(); setTimeout(() => printWindow.print(), 800); }
}

function exportToExcel(slots: TimetableSlot[], classes: ClassOption[], teachers: Teacher[], subjects: Subject[], filterType: "class" | "teacher" | "subject", filterId: string) {
  const filterName = filterType === "class" ? classes.find((c) => c.id.toString() === filterId)?.name || "Toutes les classes" : filterType === "teacher" ? teachers.find((t) => t.id.toString() === filterId)?.full_name || "Tous les enseignants" : subjects.find((s) => s.id.toString() === filterId)?.name || "Toutes les matières";
  const filteredSlots = slots.filter((s) => { if (!filterId) return true; if (filterType === "class") return s.class_id?.toString() === filterId; if (filterType === "teacher") return s.teacher_id?.toString() === filterId; if (filterType === "subject") return s.subject_id?.toString() === filterId; return true; });
  const sortedSlots = [...filteredSlots].sort((a, b) => { if (a.day_of_week !== b.day_of_week) return a.day_of_week - b.day_of_week; return a.start_time.localeCompare(b.start_time); });
  let csv = "\uFEFFLYCÉE BILINGUE DE BALENG\nEmploi du temps - " + filterName + "\nAnnée Scolaire 2025-2026\nGénéré le," + new Date().toLocaleDateString("fr-FR") + "\nJour,Horaire,Matière,Code Matière,Enseignant,Classe,Salle,Type\n";
  sortedSlots.forEach((slot) => { csv += `${DAYS[slot.day_of_week - 1]},${slot.start_time?.slice(0, 5)}-${slot.end_time?.slice(0, 5)},"${slot.subject_name || ""}","${slot.subject_code || ""}","${slot.teacher_name || ""}","${slot.class_name || ""}","${slot.classroom_name || ""}","${slot.slot_type || "course"}"\n`; });
  csv += `"Ce document est confidentiel - Lycée Bilingue de Baleng"\n"Signature du Proviseur: ___________________"\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `emploi_du_temps_${filterName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`);
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
  toast.success("Fichier CSV exporté avec succès");
}

// ==================== COMPOSANT PRINCIPAL ====================
export default function TimetablePage() {
  const { user, isAdmin, isSuperAdmin, roleName } = useAuthStore();

  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number>(() => {
    const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    return stored ? parseInt(stored) : 1;
  });
  const [selectedAcademicYearLabel, setSelectedAcademicYearLabel] = useState<string>('');

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [activeTab, setActiveTab] = useState<"timetable" | "assignments" | "auto-generate">("timetable");
  const [slots, setSlots] = useState<TimetableSlot[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");

  // ═══════════════ SCOPING PAR RÔLE ═══════════════
  const isTeacherRole = roleName === 'teacher' || roleName === 'enseignant';
  const isCenseurRole = ['censeur', 'surveillant_general', 'surveillant général', 'surveillant',
    'responsable_pedagogique', 'responsable pédagogique'].includes(roleName || '');
  const isPrivileged = isAdmin || isSuperAdmin;

  // Résoudre le teacher_id de l'utilisateur connecté (enseignant)
  const [resolvedTeacherId, setResolvedTeacherId] = useState<number | null>(null);
  useEffect(() => {
    if (!isTeacherRole) return;
    const u: any = user;
    if (u?.teacher_id) { setResolvedTeacherId(u.teacher_id); return; }
    if (!u?.id) return;
    let cancelled = false;
    apiService.get('/teachers', { params: { user_id: u.id } })
      .then((res: any) => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : (res?.data || res?.items || []);
        const t = list.find((x: any) => x.user_id === u.id) || list[0];
        if (t?.id) setResolvedTeacherId(t.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isTeacherRole, user]);

  // Classes attribuées au censeur / surveillant général
  const [allowedClassIds, setAllowedClassIds] = useState<number[]>([]);
  useEffect(() => {
    if (!isCenseurRole) { setAllowedClassIds([]); return; }
    apiService.get('/discipline/allowed-classes')
      .then((res: any) => {
        const list = Array.isArray(res) ? res : (res?.data || res?.items || []);
        setAllowedClassIds(list.map((c: any) => c.id).filter((id: any) => id != null));
      })
      .catch(() => setAllowedClassIds([]));
  }, [isCenseurRole]);

  // ─── Scoping : l'utilisateur ne voit QUE ce qui lui est autorisé ───
  const scopedSlots = useMemo(() => {
    if (isPrivileged) return slots;
    if (isTeacherRole) {
      if (!resolvedTeacherId) return [];
      return slots.filter((s) => s.teacher_id === resolvedTeacherId);
    }
    if (isCenseurRole) {
      if (allowedClassIds.length === 0) return [];
      return slots.filter((s) => s.class_id != null && allowedClassIds.includes(s.class_id));
    }
    return slots;
  }, [slots, isPrivileged, isTeacherRole, isCenseurRole, resolvedTeacherId, allowedClassIds]);

  // Options des listes déroulantes restreintes au périmètre de l'utilisateur
  const visibleClasses = useMemo(() => {
    if (isPrivileged) return classes;
    if (isTeacherRole) {
      const ids = new Set(scopedSlots.map((s) => s.class_id).filter((id): id is number => id != null));
      return classes.filter((c) => ids.has(c.id));
    }
    if (isCenseurRole) return classes.filter((c) => allowedClassIds.includes(c.id));
    return classes;
  }, [classes, scopedSlots, allowedClassIds, isPrivileged, isTeacherRole, isCenseurRole]);

  const visibleTeachers = useMemo(() => {
    // Enseignants présents dans le périmètre (son EDT pour un prof, les classes attribuées pour un censeur)
    let list = teachers;
    if (!isPrivileged) {
      const ids = new Set(scopedSlots.map((s) => s.teacher_id).filter((id): id is number => id != null));
      list = teachers.filter((t) => ids.has(t.id));
    }
    // Tri alphabétique (nom puis prénom) pour la recherche et la liste déroulante
    return [...list].sort((a, b) => {
      const nameA = (a.full_name || `${a.last_name || ""} ${a.first_name || ""}`).trim().toLowerCase();
      const nameB = (b.full_name || `${b.last_name || ""} ${b.first_name || ""}`).trim().toLowerCase();
      return nameA.localeCompare(nameB, "fr");
    });
  }, [teachers, scopedSlots, isPrivileged]);

  // ─── Filtres combinés appliqués à la grille ET à la liste ───
  const filteredSlots = useMemo(() => {
    return scopedSlots.filter((s) => {
      if (selectedClass) {
        // ✅ class_id peut être absent de la réponse (anciens slots) → repli sur class_name
        const cls = classes.find((c) => c.id.toString() === selectedClass);
        const matchesId = s.class_id != null && s.class_id?.toString() === selectedClass;
        const matchesName = (s.class_id == null) && !!cls && s.class_name === cls.name;
        if (!matchesId && !matchesName) return false;
      }
      if (selectedTeacher && s.teacher_id?.toString() !== selectedTeacher) return false;
      if (selectedDay && s.day_of_week !== parseInt(selectedDay)) return false;
      return true;
    });
  }, [scopedSlots, selectedClass, selectedTeacher, selectedDay, classes]);

  const [showSlotModal, setShowSlotModal] = useState(false);
  const [editingSlot, setEditingSlot] = useState<TimetableSlot | null>(null);
  // Modal de confirmation de suppression (créneau ou groupe de créneaux)
  const [deleteGroup, setDeleteGroup] = useState<TimetableSlot[] | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const canWrite = useMemo(() => isSuperAdmin || isAdmin || roleName === 'proviseur' || roleName === 'censeur', [isSuperAdmin, isAdmin, roleName]);

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("");

  // ─── Paramètres de génération (définissent les heures de la grille) ───
  const [genSettings, setGenSettings] = useState<GenerationSettings>(DEFAULT_GEN_SETTINGS);

  const timeRows = useMemo(() => computeTimeRows(genSettings), [genSettings]);
  const displayRows = useMemo(() => computeDisplayRows(genSettings), [genSettings]);

  const loadGenSettings = useCallback(async (yearId: number = selectedAcademicYearId) => {
    try {
      const res: any = await apiService.get('/timetable/generation-settings', {
        params: { school_id: user?.school_id || 1, academic_year_id: yearId },
      });
      if (res?.start_time && res?.end_time) {
        setGenSettings({
          start_time: res.start_time,
          end_time: res.end_time,
          break_count: typeof res.break_count === 'number' ? res.break_count : 1,
          break_duration_minutes: typeof res.break_duration_minutes === 'number' ? res.break_duration_minutes : 30,
          slot_duration_minutes: typeof res.slot_duration_minutes === 'number' ? res.slot_duration_minutes : 50,
          days: Array.isArray(res.days) && res.days.length > 0 ? res.days : [1, 2, 3, 4, 5],
        });
      }
    } catch (e) {
      // Backend indisponible → on garde les horaires officiels par défaut
      setGenSettings(DEFAULT_GEN_SETTINGS);
    }
  }, [user, selectedAcademicYearId]);

  useEffect(() => { loadGenSettings(); }, [loadGenSettings]);

  const loadSchools = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res: any = await apiService.get('/schools');
      const items = Array.isArray(res) ? res : (res?.items || res?.data || []);
      setSchools(items);
    } catch (e) { setSchools([]); }
  }, [isSuperAdmin]);

  useEffect(() => { loadSchools(); }, [loadSchools]);

  useEffect(() => {
    const handleYearChange = (e: any) => {
      const detail = e.detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(detail.year_id);
        localStorage.setItem(SELECTED_YEAR_ID_KEY, detail.year_id.toString());
        if (detail.label) setSelectedAcademicYearLabel(detail.label);
        loadData(detail.year_id);
        loadGenSettings(detail.year_id);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  const loadData = useCallback(async (yearId: number = selectedAcademicYearId, schoolFilterId?: string) => {
    setLoading(true);
    try {
      const defaultSchoolId = user?.school_id || 1;
      let effectiveSchoolId: number | undefined = schoolFilterId !== undefined 
        ? (schoolFilterId ? parseInt(schoolFilterId) : defaultSchoolId)
        : (selectedSchool ? parseInt(selectedSchool) : defaultSchoolId);
      if (isNaN(effectiveSchoolId)) effectiveSchoolId = defaultSchoolId;

      console.log('[Timetable] Loading data with school_id=', effectiveSchoolId, 'year=', yearId);

      // allSettled : une requête lente ne doit pas vider les 3 autres listes
      const endpointNames = ['slots', 'subjects', 'teachers', 'classes'];
      const settled = await Promise.allSettled([
        apiService.get('/timetable/slots', { params: { school_id: effectiveSchoolId, academic_year_id: yearId }, timeout: 60000 }) as Promise<any>,
        apiService.get('/subjects', { params: { school_id: effectiveSchoolId, academic_year_id: yearId, status: 'ACTIVE' }, timeout: 60000 }) as Promise<any>,
        apiService.get('/teachers', { params: { school_id: effectiveSchoolId, academic_year_id: yearId, status: 'active' }, timeout: 90000 }) as Promise<any>,
        apiService.get('/timetable/classes', { params: { school_id: effectiveSchoolId }, timeout: 60000 }) as Promise<any>,
      ]);
      const [slotsRes, subjectsRes, teachersRes, classesRes] = settled.map((r, i) => {
        if (r.status === 'fulfilled') return r.value;
        console.error(`[Timetable] /${endpointNames[i]} indisponible:`, r.reason);
        return [] as any;
      });

      console.log('[Timetable] classesRes:', classesRes);

      const parseResponse = (res: any): any[] => {
        if (Array.isArray(res)) return res;
        if (res && Array.isArray(res.items)) return res.items;
        if (res && Array.isArray(res.data)) return res.data;
        console.warn('[Timetable] parseResponse: no array found in', res);
        return [];
      };

      const slotsData = parseResponse(slotsRes);
      const subjectsRaw = parseResponse(subjectsRes);
      const subjectsData = subjectsRaw
        .filter((s: any) => { const st = (s.status || '').toString().toLowerCase(); return st === '' || st === 'active' || st === 'null' || st === 'undefined'; })
        .map((s: any) => {
          let section: "FR" | "EN" = "FR";
          if (s.section === "EN" || s.section === "Anglophone" || s.section_id === 2) section = "EN";
          return { id: s.id, name: s.label_fr || s.name || s.code || 'Matière sans nom', code: s.code || s.abbreviation || '', section, coefficient: s.coefficient || 1, label_fr: s.label_fr, label_en: s.label_en, section_id: s.section_id, specialty_id: s.specialty_id, status: s.status, is_active: s.is_active, school_id: s.school_id, academic_year_id: s.academic_year_id };
        });
      const teachersData = parseResponse(teachersRes);
      let classesData = parseResponse(classesRes);

      console.log('[Timetable] Parsed classes count:', classesData.length);

      if (classesData.length === 0) {
        console.log('[Timetable] FALLBACK: loading from /classes');
        try {
          const fallbackRes = await apiService.get('/classes', { params: { school_id: effectiveSchoolId } });
          classesData = parseResponse(fallbackRes);
          console.log('[Timetable] Fallback classes count:', classesData.length);
        } catch (e) { console.warn('[Timetable] Fallback /classes failed', e); }
      }

      if (classesData.length === 0) {
        console.log('[Timetable] FALLBACK 2: loading ALL classes');
        try {
          const fallbackRes2 = await apiService.get('/classes');
          classesData = parseResponse(fallbackRes2);
          console.log('[Timetable] Fallback 2 classes count:', classesData.length);
        } catch (e) { console.warn('[Timetable] Fallback 2 failed', e); }
      }

      setSlots(slotsData);
      setSubjects(subjectsData);
      setTeachers(teachersData);
      setClasses(classesData);

      if (classesData.length === 0) {
        toast.warning("Aucune classe trouvée. Vérifiez que des classes existent dans la base de données.");
      }
    } catch (err: any) {
      console.error('Load data error:', err);
      toast.error("Erreur lors du chargement des données");
    } finally { setLoading(false); }
  }, [user, selectedAcademicYearId, selectedSchool]);

  useEffect(() => { loadData(); }, []);

  const handleDeleteSlot = async (id: number) => {
    if (!canWrite) { toast.error("Permission refusée"); return; }
    if (!confirm("Supprimer ce créneau ?")) return;
    try { await apiService.delete(`/timetable/slots/${id}`); toast.success("Créneau supprimé"); loadData(); }
    catch (err) { toast.error("Erreur lors de la suppression"); }
  };

  // Suppression rapide depuis la grille — ouvre le modal de confirmation stylé
  const handleDeleteSlotGroup = (group: TimetableSlot[]) => {
    if (!canWrite) { toast.error("Permission refusée"); return; }
    setDeleteGroup(group);
  };

  // Confirmation effective : supprime tous les créneaux du groupe
  const confirmDeleteGroup = async () => {
    if (!deleteGroup) return;
    setDeleteLoading(true);
    try {
      for (const s of deleteGroup) await apiService.delete(`/timetable/slots/${s.id}`);
      toast.success(deleteGroup.length > 1
        ? `${deleteGroup.length} créneaux du groupe supprimés`
        : "Créneau supprimé");
      setDeleteGroup(null);
      loadData();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    } finally { setDeleteLoading(false); }
  };

  const getSubjectColor = (subjectName: string) => SUBJECT_COLORS_PDF[subjectName]?.bg || "bg-gray-100 text-gray-800 border-gray-200";

  const getSlotsForDayAndTime = (dayIndex: number, rowIndex: number) => {
    const rowStart = toMin(timeRows[rowIndex]);
    const rowEnd = rowIndex + 1 < timeRows.length ? toMin(timeRows[rowIndex + 1]) : Number.MAX_SAFE_INTEGER;
    return filteredSlots.filter((s) => {
      if (s.day_of_week !== dayIndex + 1) return false;
      const m = toMin((s.start_time || "00:00").slice(0, 5));
      return m >= rowStart && m < rowEnd;
    });
  };

  const handleRefresh = useCallback(() => { loadData(); }, [loadData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-7 h-7 text-[#1e3a8a]" />
              Emploi du Temps
            </h1>
            <p className="text-sm text-gray-500 mt-1">Gestion des créneaux, matières et affectations</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              <Calendar className="w-4 h-4" />
              <span className="font-medium">{selectedAcademicYearLabel || `Année ${selectedAcademicYearId}`}</span>
            </div>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button onClick={() => setViewMode("grid")} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "grid" ? "bg-white text-[#1e3a8a] shadow-sm" : "text-gray-500"}`}>
                <Grid3X3 className="w-4 h-4 inline mr-1" />Grille
              </button>
              <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === "list" ? "bg-white text-[#1e3a8a] shadow-sm" : "text-gray-500"}`}>
                <List className="w-4 h-4 inline mr-1" />Liste
              </button>
            </div>
            {canWrite && (
              <button onClick={() => { setEditingSlot(null); setShowSlotModal(true); }} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#152a5e] flex items-center gap-2 text-sm font-medium">
                <Plus className="w-4 h-4" />Nouveau Créneau
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 mt-4 border-b border-gray-200">
          {[
            { id: "timetable", label: "Emploi du Temps", icon: Calendar },
            { id: "assignments", label: "Affectations", icon: Users },
            { id: "auto-generate", label: "Génération Auto", icon: Grid3X3 },
          ].map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`px-4 py-2.5 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${activeTab === tab.id ? "border-[#1e3a8a] text-[#1e3a8a]" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {activeTab === "timetable" && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Exporter:</span>
                  <button onClick={() => exportToPDF(filteredSlots, classes, teachers, subjects, "class", selectedClass, displayRows)} className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2 border border-red-200">
                    <FileDown className="w-4 h-4" />PDF Classe
                  </button>
                  <button onClick={() => exportToExcel(filteredSlots, classes, teachers, subjects, "class", selectedClass)} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 flex items-center gap-2 border border-green-200">
                    <Table2 className="w-4 h-4" />Excel Classe
                  </button>
                </div>
                <div className="w-px h-8 bg-gray-300" />
                <div className="flex items-center gap-2">
                  <button onClick={() => exportToPDF(filteredSlots, classes, teachers, subjects, "teacher", selectedTeacher || (resolvedTeacherId ? String(resolvedTeacherId) : ""), displayRows)} className="px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2 border border-blue-200">
                    <FileDown className="w-4 h-4" />PDF Prof
                  </button>
                  <button onClick={() => exportToExcel(filteredSlots, classes, teachers, subjects, "teacher", selectedTeacher || (resolvedTeacherId ? String(resolvedTeacherId) : ""))} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 flex items-center gap-2 border border-green-200">
                    <Table2 className="w-4 h-4" />Excel Prof
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
              <div className="flex flex-wrap items-center gap-3">
                {isSuperAdmin && (
                  <select value={selectedSchool} onChange={(e) => { const newSchoolId = e.target.value; setSelectedSchool(newSchoolId); loadData(selectedAcademicYearId, newSchoolId); }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                    <option value="">Tous les établissements</option>
                    {schools.map((s) => (<option key={s.id} value={s.id}>{s.acronym || s.name}</option>))}
                  </select>
                )}
                {isTeacherRole ? (
                  <span className="px-3 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium">Mon emploi du temps</span>
                ) : (
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">Toutes les classes</option>
                  {visibleClasses.length === 0 ? <option value="" disabled>{isCenseurRole && allowedClassIds.length === 0 ? 'Aucune classe attribuée' : 'Chargement...'}</option> : visibleClasses.map((c) => (<option key={c.id} value={c.id}>{c.name} {c.level_name ? `(${c.level_name})` : ''}</option>))}
                </select>
                )}
                {isTeacherRole ? null : (
                <div className="min-w-[220px]">
                  <SearchableSelect
                    value={selectedTeacher}
                    onChange={(val) => setSelectedTeacher(val)}
                    placeholder="Tous les enseignants"
                    options={visibleTeachers.map((t) => ({ id: t.id, label: t.full_name || `${t.first_name} ${t.last_name}` }))}
                  />
                </div>
                )}
                <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">Tous les jours</option>
                  {DAYS.map((day, i) => (<option key={i} value={i + 1}>{day}</option>))}
                </select>
                <button onClick={() => { setSelectedClass(""); setSelectedTeacher(""); setSelectedDay(""); setSelectedSchool(""); loadData(selectedAcademicYearId, ""); }} className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm">Réinitialiser</button>
                <span className="ml-auto text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">{filteredSlots.length} créneau{filteredSlots.length > 1 ? 'x' : ''} affiché{filteredSlots.length > 1 ? 's' : ''}</span>
              </div>
              {classes.length === 0 && !loading && (
                <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                  ⚠️ Aucune classe chargée. Vérifiez la console (F12) pour les détails.
                </div>
              )}
            </div>

            {viewMode === "grid" ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr>
                        <th className="px-3 py-3 bg-gray-50 border-b border-r border-gray-200 text-xs font-semibold text-gray-600 w-20">Heure</th>
                        {DAYS.map((day) => (<th key={day} className="px-3 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-600 min-w-[180px]">{day}</th>))}
                      </tr>
                    </thead>
                    <tbody>
                      {displayRows.map((row, displayIdx) => row.isBreak ? (
                        <tr key={`break-${displayIdx}`} className="border-b border-gray-100">
                          <td className="px-3 py-2 bg-amber-50 border-r border-amber-200 text-center">☕</td>
                          <td colSpan={DAYS.length} className="px-3 py-1.5 bg-amber-50 border-r border-amber-100 text-center text-xs font-bold text-amber-700 tracking-widest">
                            PAUSE {row.label}
                          </td>
                        </tr>
                      ) : (
                        <tr key={row.label} className="border-b border-gray-100">
                          <td className="px-3 py-2 bg-gray-50 border-r border-gray-200 text-xs font-medium text-gray-600 text-center">{row.label}</td>
                          {DAYS.map((_, dayIdx) => {
                            const daySlots = getSlotsForDayAndTime(dayIdx, row.slotIndex);
                            return (
                              <td key={dayIdx} className="px-1 py-1 border-r border-gray-100 min-h-[80px] align-top">
                                <div className="space-y-1">
                                  {(() => {
                                    // ─── Fusion des créneaux groupés (même enseignant/matière/heure sur plusieurs classes) ───
                                    const byKey = new Map<string, TimetableSlot[]>();
                                    daySlots.forEach((s) => {
                                      const key = `${s.teacher_id}|${s.subject_id}|${s.start_time}|${s.end_time}`;
                                      if (!byKey.has(key)) byKey.set(key, []);
                                      byKey.get(key)!.push(s);
                                    });
                                    return Array.from(byKey.values()).map((group) => {
                                      const slot = group[0];
                                      const classNames = Array.from(new Set(group.map((g) => g.class_name).filter(Boolean)));
                                      const isGroup = group.length > 1;
                                      return (
                                    <div key={group.map((g) => g.id).join('-')} className={`relative rounded-lg border p-2 text-xs cursor-pointer hover:shadow-md transition-shadow ${getSubjectColor(slot.subject_name || "")} ${isGroup ? 'ring-2 ring-purple-400 ring-offset-1' : ''}`} onClick={() => { if (canWrite) { setEditingSlot(slot); setShowSlotModal(true); }}}>
                                      {canWrite && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteSlotGroup(group); }}
                                          className="absolute -top-1.5 -right-1.5 w-4 h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none hover:bg-red-700 shadow transition-colors"
                                          title={group.length > 1 ? `Supprimer le créneau groupe (${group.length} classes)` : "Supprimer ce créneau"}
                                        >×</button>
                                      )}
                                      <p className="font-semibold truncate flex items-center gap-1">{slot.subject_name}{isGroup && <span className="text-[9px] bg-purple-600 text-white px-1.5 py-0.5 rounded-full font-bold">👥 GROUPE ×{group.length}</span>}</p>
                                      <p className="truncate opacity-80">{slot.teacher_name}</p>
                                      <p className="truncate opacity-70 flex items-center gap-1"><MapPin className="w-3 h-3" />{classNames.join(' · ') || "Classe non définie"}</p>
                                      <p className="truncate opacity-60 text-[10px] flex items-center gap-1">🚪 {slot.classroom_name || "Salle non attribuée"}</p>
                                      <div className="flex items-center justify-between mt-1">
                                        <span className="text-[10px] opacity-60">{slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}</span>
                                      </div>
                                    </div>
                                    );
                                    });
                                  })()}
                                  {daySlots.length === 0 && canWrite && (
                                    <div className="h-16 flex items-center justify-center">
                                      <button onClick={() => {
                                        const slotDur = genSettings.slot_duration_minutes || 50;
                                        setEditingSlot({ id: 0, day_of_week: dayIdx + 1, start_time: row.label, end_time: toHHMM(toMin(row.label) + slotDur), subject_id: 0, teacher_id: selectedTeacher ? parseInt(selectedTeacher) : 0, class_id: selectedClass ? parseInt(selectedClass) : 0 } as TimetableSlot);
                                        setShowSlotModal(true);
                                      }} className="w-full h-full flex items-center justify-center text-gray-300 hover:text-[#1e3a8a] hover:bg-blue-50 rounded-lg transition-colors">
                                        <Plus className="w-4 h-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Jour</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Horaire</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Matière</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Enseignant</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Classe</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Salle</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" /> Chargement...</td></tr>
                    ) : filteredSlots.length === 0 ? (
                      <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">Aucun créneau</td></tr>
                    ) : (
                      filteredSlots.map((slot) => (
                        <tr key={slot.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{DAYS[slot.day_of_week - 1]}</td>
                          <td className="px-4 py-3 text-sm text-gray-600"><Clock className="w-3.5 h-3.5 inline mr-1" />{slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}</td>
                          <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium border ${getSubjectColor(slot.subject_name || "")}`}>{slot.subject_name}</span></td>
                          <td className="px-4 py-3 text-sm text-gray-700">{slot.teacher_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{slot.class_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{slot.classroom_name || "-"}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {canWrite && (
                                <>
                                  <button onClick={() => { setEditingSlot(slot); setShowSlotModal(true); }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => handleDeleteSlot(slot.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === "assignments" && (
          <AssignmentsTab 
            classes={classes} teachers={teachers} subjects={subjects} 
            academicYearId={selectedAcademicYearId}
            schoolId={selectedSchool ? parseInt(selectedSchool) : (user?.school_id || 1)}
            selectedSchool={selectedSchool} schools={schools} isSuperAdmin={isSuperAdmin}
            onSchoolChange={(schoolId: string) => { setSelectedSchool(schoolId); loadData(selectedAcademicYearId, schoolId); }}
            onRefresh={handleRefresh} canWrite={canWrite}
          />
        )}

        {activeTab === "auto-generate" && (
          <AutoGenerateTab
            classes={classes}
            subjects={subjects}
            teachers={teachers}
            academicYearId={selectedAcademicYearId}
            schoolId={selectedSchool ? parseInt(selectedSchool) : (user?.school_id || 1)}
            onSuccess={() => { setActiveTab("timetable"); loadData(); }}
            canWrite={canWrite}
          />
        )}
      </div>

      {/* ═══ Modal de confirmation de suppression (design) ═══ */}
      {deleteGroup && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !deleteLoading && setDeleteGroup(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* En-tête rouge */}
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-white/20 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {deleteGroup.length > 1 ? 'Supprimer le créneau groupe' : 'Supprimer le créneau'}
                  </h3>
                  <p className="text-xs text-red-100">
                    {deleteGroup.length > 1
                      ? `${deleteGroup.length} classes concernées — action irréversible`
                      : 'Cette action est irréversible'}
                  </p>
                </div>
              </div>
            </div>

            {/* Corps : récapitulatif du créneau */}
            <div className="p-6">
              {(() => {
                const s = deleteGroup[0];
                const dayName = DAYS[s.day_of_week - 1] || `Jour ${s.day_of_week}`;
                const classNames = Array.from(new Set(deleteGroup.map((g) => g.class_name).filter(Boolean)));
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <BookOpen className="w-4 h-4 text-[#1e3a8a] shrink-0" />
                      <span className="font-semibold text-gray-900">{s.subject_name || 'Matière'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <GraduationCap className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{s.teacher_name || 'Enseignant'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                      <span>{dayName} · {s.start_time?.slice(0, 5)} – {s.end_time?.slice(0, 5)}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-gray-600 pt-1 border-t border-gray-200 mt-2">
                      <MapPin className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {classNames.length > 0 ? classNames.map((n) => (
                          <span key={n} className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-medium">{n}</span>
                        )) : <span className="text-gray-400">Classe non définie</span>}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <p className="text-sm text-gray-500 mt-4">
                {deleteGroup.length > 1
                  ? 'Tous les créneaux de ce groupe seront supprimés simultanément.'
                  : "Voulez-vous vraiment supprimer ce créneau de l'emploi du temps ?"}
              </p>
            </div>

            {/* Pied : actions */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setDeleteGroup(null)}
                disabled={deleteLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={confirmDeleteGroup}
                disabled={deleteLoading}
                className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg text-sm font-medium hover:from-red-700 hover:to-red-800 transition-all shadow-md shadow-red-200 flex items-center gap-2 disabled:opacity-50"
              >
                {deleteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleteLoading ? 'Suppression...' : deleteGroup.length > 1 ? `Supprimer ${deleteGroup.length} créneaux` : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSlotModal && (
        <SlotModal 
          slot={editingSlot} subjects={subjects} teachers={teachers} classes={classes} 
          academicYearId={selectedAcademicYearId} schoolId={user?.school_id || 1}
          onClose={() => { setShowSlotModal(false); setEditingSlot(null); }}
          onSuccess={() => { loadData(); setShowSlotModal(false); setEditingSlot(null); }} 
        
          allSlots={slots}
        />
      )}
    </div>
  );
}

// ==================== SLOT MODAL (WIZARD 6 ÉTAPES) ====================
function SlotModal({ slot, subjects: allSubjects, teachers, classes, academicYearId, schoolId, onClose, onSuccess, allSlots }: {
  slot: TimetableSlot | null; subjects: Subject[]; teachers: Teacher[]; classes: ClassOption[];
  academicYearId: number; schoolId: number; onClose: () => void; onSuccess: () => void;
  allSlots: TimetableSlot[];
}) {
  // ─── Wizard state ───
  const [step, setStep] = useState(1);
  const isEditing = !!slot?.id;

  // ─── Sélections ───
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(slot?.teacher_id || null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(slot?.class_id || null);
  // ─── Classes groupées (cours en commun : même enseignant, même heure) ───
  const [groupedClassIds, setGroupedClassIds] = useState<number[]>([]);
  // Classes du même niveau que la classe choisie (candidates au regroupement)
  const groupableClasses = useMemo(() => {
    if (!selectedClassId) return [];
    const selected = classes.find((c) => c.id === selectedClassId);
    if (!selected || selected.level_id == null) return [];
    return classes.filter((c) => c.id !== selectedClassId && c.level_id === selected.level_id);
  }, [selectedClassId, classes]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(slot?.subject_id || null);

  // ─── Données chargées ───
  const [teacherAssignments, setTeacherAssignments] = useState<any[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [availableSubjects, setAvailableSubjects] = useState<any[]>([]);
  const [hoursUsedMap, setHoursUsedMap] = useState<Record<number, number>>({});

  // ─── Formulaire étape 4-5 ───
  const [formData, setFormData] = useState({
    day_of_week: slot?.day_of_week || 1,
    start_time: slot?.start_time?.slice(0, 5) || "07:30",
    end_time: slot?.end_time?.slice(0, 5) || "08:30",
    room: slot?.classroom_name || "",
    slot_type: slot?.slot_type || "course",
    notes: slot?.notes || "",
  });

  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);

  // ─── Helpers heures ───
  const calculateUsedHours = useCallback((classId: number, subjectId: number, slots: TimetableSlot[]) => {
    const relevant = slots.filter(s => s.class_id === classId && s.subject_id === subjectId && s.id !== (slot?.id || 0));
    let totalMinutes = 0;
    relevant.forEach(s => {
      const [sh, sm] = s.start_time.split(':').map(Number);
      const [eh, em] = s.end_time.split(':').map(Number);
      totalMinutes += (eh * 60 + em) - (sh * 60 + sm);
    });
    return totalMinutes / 60;
  }, [slot]);

  const calculateSlotHours = useCallback(() => {
    const [sh, sm] = formData.start_time.split(':').map(Number);
    const [eh, em] = formData.end_time.split(':').map(Number);
    return ((eh * 60 + em) - (sh * 60 + sm)) / 60;
  }, [formData.start_time, formData.end_time]);

  // ─── Étape 1 : charger assignments quand enseignant change ───
  const loadTeacherAssignments = useCallback(async (teacherId: number) => {
    setLoadingAssignments(true);
    setWarning(null);
    try {
      const res: any = await apiService.get('/timetable/assignments', {
        params: { teacher_id: teacherId, academic_year_id: academicYearId, school_id: schoolId, status: 'ACTIVE' }
      });
      const data = Array.isArray(res) ? res : (res?.items || res?.data || []);
      setTeacherAssignments(data);
    } catch (e) {
      console.warn('Load teacher assignments error', e);
      setTeacherAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, [academicYearId, schoolId]);

  useEffect(() => {
    if (selectedTeacherId) {
      loadTeacherAssignments(selectedTeacherId);
    } else {
      setTeacherAssignments([]);
    }
  }, [selectedTeacherId, loadTeacherAssignments]);

  // ─── Classes disponibles pour l'enseignant ───
  const teacherClasses = useMemo(() => {
    if (!selectedTeacherId || teacherAssignments.length === 0) return [];
    const classIds = [...new Set(teacherAssignments.map((a: any) => a.class_id).filter(Boolean))];
    return classes.filter(c => classIds.includes(c.id));
  }, [selectedTeacherId, teacherAssignments, classes]);

  // ─── Étape 2 → 3 : quand classe sélectionnée, préparer matières ───
  useEffect(() => {
    if (!selectedClassId || !selectedTeacherId) {
      setAvailableSubjects([]);
      setHoursUsedMap({});
      return;
    }
    const relevantAssignments = teacherAssignments.filter((a: any) => a.class_id === selectedClassId);
    const subjectsWithHours = relevantAssignments.map((a: any) => ({
      id: a.subject_id,
      name: a.subject_name || a.subject_code || 'Matière',
      code: a.subject_code || '',
      hours_per_week: parseFloat(a.hours_per_week) || 0,
    }));
    // Calculer heures utilisées pour chaque matière
    const usedMap: Record<number, number> = {};
    subjectsWithHours.forEach((s: any) => {
      usedMap[s.id] = calculateUsedHours(selectedClassId, s.id, allSlots);
    });
    setAvailableSubjects(subjectsWithHours);
    setHoursUsedMap(usedMap);
  }, [selectedClassId, selectedTeacherId, teacherAssignments, allSlots, calculateUsedHours]);

  // ─── Vérification heures quand matière ou horaire change ───
  useEffect(() => {
    setWarning(null);
    if (!selectedSubjectId || !selectedClassId) return;
    const subjectInfo = availableSubjects.find((s: any) => s.id === selectedSubjectId);
    if (!subjectInfo) return;
    const used = hoursUsedMap[selectedSubjectId] || 0;
    const newSlotHrs = calculateSlotHours();
    const total = used + newSlotHrs;
    const limit = subjectInfo.hours_per_week || 0;
    if (limit > 0 && total > limit) {
      setWarning(`⚠️ Heures dépassées : ${total.toFixed(1)}h / ${limit}h configurées pour ${subjectInfo.name}.`);
    } else if (limit > 0 && total === limit) {
      setWarning(`✅ Dernière heure : vous atteignez exactement ${limit}h pour ${subjectInfo.name}.`);
    }
  }, [selectedSubjectId, selectedClassId, formData.start_time, formData.end_time, availableSubjects, hoursUsedMap, calculateSlotHours]);

  // ─── Navigation ───
  const canGoNext = useCallback(() => {
    switch (step) {
      case 1: return !!selectedTeacherId;
      case 2: return !!selectedClassId;
      case 3: return !!selectedSubjectId;
      case 4: return !!formData.start_time && !!formData.end_time && formData.start_time < formData.end_time;
      case 5: return true;
      default: return false;
    }
  }, [step, selectedTeacherId, selectedClassId, selectedSubjectId, formData]);

  const nextStep = () => { if (canGoNext()) setStep(s => Math.min(s + 1, 6)); };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  // ─── Pré-remplissage édition ───
  useEffect(() => {
    if (isEditing && selectedTeacherId && selectedClassId && selectedSubjectId) {
      // Si on a toutes les sélections, on saute à l'étape 4 (horaire)
      setStep(4);
    }
  }, [isEditing, selectedTeacherId, selectedClassId, selectedSubjectId]);

  // ─── Submit ───
  const handleSubmit = async () => {
    if (!selectedTeacherId || !selectedSubjectId || !selectedClassId) {
      toast.error("Veuillez compléter toutes les étapes"); return;
    }
    // ─── Vérification stricte du quota hebdomadaire avant sauvegarde ───
    const quotaInfo = availableSubjects.find((s: any) => s.id === selectedSubjectId);
    if (quotaInfo) {
      const used = hoursUsedMap[selectedSubjectId] || 0;
      const slotHrs = calculateSlotHours();
      const limit = parseFloat(quotaInfo.hours_per_week) || 0;
      if (limit > 0 && used + slotHrs > limit + 0.001) {
        toast.error(
          `Quota dépassé pour ${quotaInfo.name} : ${(used + slotHrs).toFixed(1)}h / ${limit}h hebdomadaires. ` +
          `Réduisez la durée du créneau ou augmentez le quota dans les affectations.`
        );
        setSaving(false);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        timetable_id: parseInt(selectedClassId.toString()) || 1,
        class_id: parseInt(selectedClassId.toString()) || 1,
        day_of_week: parseInt(formData.day_of_week.toString()),
        start_time: formData.start_time,
        end_time: formData.end_time,
        subject_id: parseInt(selectedSubjectId.toString()),
        teacher_id: parseInt(selectedTeacherId.toString()),
        room: formData.room || `Salle ${selectedClassId}`,
        slot_type: formData.slot_type || "course",
        is_recurring: true,
        academic_year_id: academicYearId,
        school_id: schoolId,
        // ─── Cours en commun : le backend duplique le créneau pour chaque classe cochée ───
        ...(slot?.id ? {} : { group_class_ids: groupedClassIds }),
      };
      if (slot?.id) {
        await apiService.put(`/timetable/slots/${slot.id}`, payload);
        toast.success("Créneau mis à jour");
      } else {
        await apiService.post('/timetable/slots', payload);
        toast.success("Créneau créé");
      }
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  // ─── Rendu stepper ───
  const steps = [
    { num: 1, label: "Enseignant" },
    { num: 2, label: "Classe" },
    { num: 3, label: "Matière" },
    { num: 4, label: "Horaire" },
    { num: 5, label: "Détails" },
    { num: 6, label: "Récap" },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditing ? "Modifier le Créneau" : "Nouveau Créneau"}
            </h2>
            <p className="text-xs text-gray-500">Étape {step} sur 6 — {steps[step-1].label}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        {/* Stepper visuel */}
        <div className="px-6 pt-4 flex items-center gap-1 flex-shrink-0">
          {steps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className={`flex-1 h-1.5 rounded-full transition-colors ${step >= s.num ? 'bg-[#1e3a8a]' : 'bg-gray-200'}`} />
              {idx < steps.length - 1 && <div className="w-1" />}
            </React.Fragment>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ─── STEP 1 : ENSEIGNANT ─── */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">👨‍🏫 Étape 1 : Sélectionnez l'enseignant</p>
                <p className="text-xs mt-1">Seuls les enseignants avec des affectations actives seront proposés.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enseignant *</label>
                <select
                  value={selectedTeacherId || ""}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedTeacherId(val);
                    setSelectedClassId(null);
                    setSelectedSubjectId(null);
                    setStep(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]"
                >
                  <option value="">Sélectionner un enseignant...</option>
                  {teachers.map((t) => (
                    <option key={t.id} value={t.id}>{t.full_name || `${t.first_name} ${t.last_name}`}</option>
                  ))}
                </select>
              </div>
              {loadingAssignments && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" /> Chargement des affectations...
                </div>
              )}
              {selectedTeacherId && teacherAssignments.length === 0 && !loadingAssignments && (
                <div className="p-3 bg-amber-50 text-amber-700 rounded-lg text-sm">
                  ⚠️ Cet enseignant n'a aucune affectation active. Veuillez d'abord créer une affectation dans l'onglet "Affectations".
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 2 : CLASSE ─── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">🏫 Étape 2 : Sélectionnez la classe</p>
                <p className="text-xs mt-1">Seules les classes assignées à {teachers.find(t => t.id === selectedTeacherId)?.full_name || "cet enseignant"} sont affichées.</p>
              </div>
              {teacherClasses.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Aucune classe assignée à cet enseignant.</div>
              ) : (
                <div className="space-y-2">
                  {teacherClasses.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setSelectedClassId(c.id); setSelectedSubjectId(null); }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${selectedClassId === c.id ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{c.name}</p>
                        <p className="text-xs text-gray-500">{c.level_name || ''} {c.abbreviation || ''}</p>
                      </div>
                      {selectedClassId === c.id && <CheckCircle className="w-5 h-5 text-blue-600" />}
                    </button>
                  ))}

                  {/* ─── Cours en commun (classes groupées) ─── */}
                  {selectedClassId && groupableClasses.length > 0 && (
                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-purple-800">👥 Cours en commun (classes groupées)</p>
                      <p className="text-xs text-purple-600 mt-0.5 mb-2">Le même créneau sera créé pour les classes cochées — un seul enseignant, à la même heure (ex. EPS LowerSixth A1+A2+A3+A4).</p>
                      <div className="space-y-1.5 max-h-40 overflow-y-auto">
                        {groupableClasses.map((c) => (
                          <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer hover:bg-purple-100/60 rounded px-1 py-0.5">
                            <input
                              type="checkbox"
                              checked={groupedClassIds.includes(c.id)}
                              onChange={(e) => setGroupedClassIds((prev) => e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id))}
                              className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                            />
                            <span>{c.name} <span className="text-xs text-gray-400">{c.level_name || ''}</span></span>
                          </label>
                        ))}
                      </div>
                      {groupedClassIds.length > 0 && (
                        <p className="text-xs text-purple-700 mt-2 font-medium">📌 Créneau groupé sur {groupedClassIds.length + 1} classes</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 3 : MATIÈRE ─── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">📚 Étape 3 : Sélectionnez la matière</p>
                <p className="text-xs mt-1">Les heures déjà placées et le maximum configuré sont affichés.</p>
              </div>
              {availableSubjects.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Aucune matière assignée pour cette classe.</div>
              ) : (
                <div className="space-y-2">
                  {availableSubjects.map((s: any) => {
                    const used = hoursUsedMap[s.id] || 0;
                    const limit = s.hours_per_week || 0;
                    const remaining = Math.max(0, limit - used);
                    const isFull = limit > 0 && remaining <= 0;
                    const isSelected = selectedSubjectId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => !isFull && setSelectedSubjectId(s.id)}
                        disabled={isFull}
                        className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors ${isSelected ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-300' : isFull ? 'bg-gray-50 border-gray-100 opacity-60 cursor-not-allowed' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{s.name} {s.code ? `(${s.code})` : ''}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{used.toFixed(1)}h / {limit > 0 ? `${limit}h` : '∞'} configurées</span>
                            {limit > 0 && (
                              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${remaining > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {remaining > 0 ? `${remaining.toFixed(1)}h restantes` : 'Complet'}
                              </span>
                            )}
                          </div>
                          {/* Barre de progression (couleur selon le niveau de remplissage) */}
                          {limit > 0 && (
                            <div className="w-full h-1.5 bg-gray-100 rounded-full mt-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  used >= limit ? 'bg-red-500' : used >= limit * 0.8 ? 'bg-amber-500' : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.min(100, (used / limit) * 100)}%` }}
                              />
                            </div>
                          )}
                        </div>
                        {isSelected && <CheckCircle className="w-5 h-5 text-blue-600 ml-2" />}
                        {isFull && <span className="text-xs text-red-500 ml-2 font-medium">Complet</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 4 : HORAIRE ─── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">⏰ Étape 4 : Définissez l'horaire</p>
                <p className="text-xs mt-1">Durée du créneau : {calculateSlotHours().toFixed(1)} heure(s)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jour *</label>
                <select value={formData.day_of_week} onChange={(e) => setFormData({ ...formData, day_of_week: parseInt(e.target.value) })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]">
                  {DAYS.map((day, i) => <option key={i} value={i + 1}>{day}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Début *</label>
                  <input type="time" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Fin *</label>
                  <input type="time" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" />
                </div>
              </div>
              {/* Bandeau de quota en direct : se met à jour quand on modifie les heures */}
              {selectedSubjectId && (() => {
                const info = availableSubjects.find((s: any) => s.id === selectedSubjectId);
                if (!info || !(parseFloat(info.hours_per_week) > 0)) return null;
                const used = hoursUsedMap[selectedSubjectId] || 0;
                const newHrs = calculateSlotHours();
                const limit = parseFloat(info.hours_per_week);
                const total = used + newHrs;
                const pct = Math.min(100, (total / limit) * 100);
                return (
                  <div className={`p-3 rounded-lg border transition-colors ${
                    total > limit ? 'bg-red-50 border-red-200' :
                    total >= limit * 0.8 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
                  }`}>
                    <div className="flex items-center justify-between text-xs font-medium mb-1.5">
                      <span className="text-gray-700">Quota {info.name} — {total.toFixed(1)}h / {limit}h par semaine</span>
                      <span className={total > limit ? 'text-red-600 font-semibold' : total >= limit * 0.8 ? 'text-amber-600 font-semibold' : 'text-blue-600'}>
                        {total > limit
                          ? `Dépassement de ${(total - limit).toFixed(1)}h`
                          : total >= limit
                            ? 'Quota atteint'
                            : `${(limit - total).toFixed(1)}h restantes`}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white rounded-full overflow-hidden border border-gray-200">
                      <div
                        className={`h-full rounded-full transition-all ${
                          total > limit ? 'bg-red-500' : total >= limit * 0.8 ? 'bg-amber-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
              {warning && (
                <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${warning.startsWith('⚠️') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  <TriangleAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{warning}</span>
                </div>
              )}
            </div>
          )}

          {/* ─── STEP 5 : DÉTAILS ─── */}
          {step === 5 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">📝 Étape 5 : Détails du créneau</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de créneau</label>
                <select value={formData.slot_type} onChange={(e) => setFormData({ ...formData, slot_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="course">Cours</option>
                  <option value="td">TD</option>
                  <option value="tp">TP</option>
                  <option value="exam">Examen</option>
                  <option value="break">Pause</option>
                  <option value="extracurricular">Extra-scolaire</option>
                  <option value="meeting">Réunion</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Salle</label>
                <input type="text" value={formData.room} onChange={(e) => setFormData({ ...formData, room: e.target.value })} placeholder={`Salle ${selectedClassId || '101'}`} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] text-sm" placeholder="Notes optionnelles..." />
              </div>
            </div>
          )}

          {/* ─── STEP 6 : RÉCAP ─── */}
          {step === 6 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p className="font-medium">✅ Étape 6 : Vérifiez et confirmez</p>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Enseignant</span>
                  <span className="text-sm font-medium text-gray-900">{teachers.find(t => t.id === selectedTeacherId)?.full_name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Classe</span>
                  <span className="text-sm font-medium text-gray-900">{classes.find(c => c.id === selectedClassId)?.name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Matière</span>
                  <span className="text-sm font-medium text-gray-900">{allSubjects.find(s => s.id === selectedSubjectId)?.name || availableSubjects.find((s:any) => s.id === selectedSubjectId)?.name || '-'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Jour</span>
                  <span className="text-sm font-medium text-gray-900">{DAYS[formData.day_of_week - 1]}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Horaire</span>
                  <span className="text-sm font-medium text-gray-900">{formData.start_time} — {formData.end_time} ({calculateSlotHours().toFixed(1)}h)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Salle</span>
                  <span className="text-sm font-medium text-gray-900">{formData.room || `Salle ${selectedClassId}`}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-sm text-gray-500">Type</span>
                  <span className="text-sm font-medium text-gray-900 capitalize">{formData.slot_type}</span>
                </div>
                {warning && (
                  <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${warning.startsWith('⚠️') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                    <TriangleAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{warning}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer navigation */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-between flex-shrink-0">
          <div>
            {step > 1 && (
              <button onClick={prevStep} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
                ← Précédent
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
              Annuler
            </button>
            {step < 6 ? (
              <button
                onClick={nextStep}
                disabled={!canGoNext()}
                className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Suivant →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center gap-2 transition-colors"
              >
                {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {isEditing ? "Mettre à jour" : "Créer le créneau"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
// ==================== ASSIGNMENTS TAB ====================
// ─── Dropdown avec recherche intégrée (pour les listes longues : enseignants, etc.) ───
function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Sélectionner...",
  required = false,
}: {
  value: string | number;
  onChange: (val: string) => void;
  options: { id: number; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => String(o.id) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left text-sm bg-white hover:border-[#1e3a8a] flex items-center justify-between gap-2">
        <span className={selected ? "text-gray-900 truncate" : "text-gray-400 truncate"}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus type="text" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Taper pour rechercher..."
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]" />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-gray-400 italic">Aucun résultat pour "{query}"</p>
            ) : filtered.map((o) => (
              <button key={o.id} type="button"
                onClick={() => { onChange(String(o.id)); setOpen(false); setQuery(""); }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${String(o.id) === String(value) ? "bg-blue-100 text-[#1e3a8a] font-medium" : "text-gray-700 hover:bg-blue-50"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Garde la validation native du formulaire (champ requis caché) */}
      {required && <input type="text" required value={value ? "ok" : ""} className="absolute opacity-0 pointer-events-none h-0 w-0" tabIndex={-1} aria-hidden="true" />}
    </div>
  );
}

function AssignmentsTab({ classes, teachers, subjects, academicYearId, schoolId, selectedSchool, schools, isSuperAdmin, onSchoolChange, onRefresh, canWrite }: {
  classes: ClassOption[]; teachers: Teacher[]; subjects: Subject[];
  academicYearId: number; schoolId: number; selectedSchool: string; schools: School[];
  isSuperAdmin: boolean; onSchoolChange: (schoolId: string) => void; onRefresh: () => void; canWrite: boolean;
}) {
  const [assignments, setAssignments] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number; name: string }>({ show: false, id: 0, name: '' });

  // V2 : useCallback STABLE — appelable directement après création/modif.
  // (Avant : rechargement déclenché par le useEffect via onRefresh, qui ne
  // changeait jamais de référence → refresh manuel obligatoire.)
  const loadAssignments = useCallback(async () => {
    try {
      const res: any = await apiService.get('/timetable/assignments', { params: { school_id: schoolId, academic_year_id: academicYearId } });
      const data = Array.isArray(res) ? res : Array.isArray(res?.items) ? res.items : Array.isArray(res?.data) ? res.data : [];
      setAssignments(data);
    } catch (err) { setAssignments([]); }
  }, [schoolId, academicYearId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const handleDeleteAssignment = (id: number, name: string) => {
    if (!canWrite) { toast.error("Permission refusée"); return; }
    setDeleteConfirm({ show: true, id, name });
  };

  const confirmDeleteAssignment = async () => {
    if (!canWrite) { toast.error("Permission refusée"); setDeleteConfirm({ show: false, id: 0, name: '' }); return; }
    if (!deleteConfirm.id) return;
    try {
      await apiService.delete(`/timetable/assignments/${deleteConfirm.id}`);
      toast.success("Affectation supprimée");
      setDeleteConfirm({ show: false, id: 0, name: '' });
      await loadAssignments();
      onRefresh();
    } catch (err: any) { toast.error(err.response?.data?.detail || "Erreur lors de la suppression"); }
  };

  const filteredAssignments = assignments.filter((a) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (a.teacher_name || "").toLowerCase().includes(term) || (a.subject_name || "").toLowerCase().includes(term) || (a.class_name || "").toLowerCase().includes(term);
  });

  return (
    <div className="space-y-4">
      {isSuperAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Établissement:</span>
            <select value={selectedSchool} onChange={(e) => onSchoolChange(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
              <option value="">Tous les établissements</option>
              {schools.map((s) => (<option key={s.id} value={s.id}>{s.acronym || s.name}</option>))}
            </select>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Affectations Enseignants</h2>
        {canWrite && (
          <button onClick={() => setShowModal(true)} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg hover:bg-[#152a5e] flex items-center gap-2 text-sm font-medium">
            <Plus className="w-4 h-4" />Nouvelle Affectation
          </button>
        )}
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Rechercher une affectation (enseignant, matière, classe...)" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-transparent" />
          {searchTerm && (<button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>)}
        </div>
        <div className="mt-2 text-xs text-gray-500">{filteredAssignments.length} affectation{filteredAssignments.length > 1 ? 's' : ''} trouvée{filteredAssignments.length > 1 ? 's' : ''} {searchTerm ? `(sur ${assignments.length})` : ''}</div>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Enseignant</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Matière</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Classe</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Heures</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Principal</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredAssignments.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-500">Aucune affectation</td></tr>
            ) : (
              filteredAssignments.map((a) => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{a.teacher_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{a.subject_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{a.class_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{a.hours_per_week || "-"}h</td>
                  <td className="px-4 py-3">{a.is_principal_teacher ? <CheckCircle className="w-5 h-5 text-green-500" /> : <span className="text-gray-300">-</span>}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canWrite && (
                        <>
                          <button onClick={() => { setEditingAssignment(a); setShowModal(true); }} className="p-1.5 hover:bg-amber-50 rounded-lg text-gray-500 hover:text-amber-600 transition-colors" title="Modifier"><Edit2 className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteAssignment(a.id, `${a.teacher_name} - ${a.subject_name} (${a.class_name})`)} className="p-1.5 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition-colors" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {showModal && (
        <AssignmentModal classes={classes} teachers={teachers} subjects={subjects} academicYearId={academicYearId} schoolId={schoolId} assignment={editingAssignment}
          onClose={() => { setShowModal(false); setEditingAssignment(null); }}
          onSuccess={() => { loadAssignments(); onRefresh(); setShowModal(false); setEditingAssignment(null); }}
        />
      )}
      {deleteConfirm.show && (
        <DeleteConfirmModal title="Supprimer l'affectation" message={`Êtes-vous sûr de vouloir supprimer l'affectation « ${deleteConfirm.name} » ? Cette action est irréversible.`}
          onConfirm={confirmDeleteAssignment} onCancel={() => setDeleteConfirm({ show: false, id: 0, name: '' })}
        />
      )}
    </div>
  );
}

// ==================== ASSIGNMENT MODAL ====================
function AssignmentModal({ classes, teachers, subjects, academicYearId, schoolId, assignment, onClose, onSuccess }: {
  classes: ClassOption[]; teachers: Teacher[]; subjects: Subject[]; academicYearId: number; schoolId: number; assignment?: any; onClose: () => void; onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({ 
    teacher_id: assignment?.teacher_id?.toString() || "", 
    subject_id: assignment?.subject_id?.toString() || "", 
    selected_subject_ids: assignment?.subject_id ? [assignment.subject_id] : [] as number[], 
    selected_class_ids: assignment?.class_id ? [assignment.class_id] : [] as number[], 
    is_principal: assignment?.is_principal_teacher || false 
  });
  const [saving, setSaving] = useState(false);
  const [saveMode, setSaveMode] = useState<'single' | 'batch'>('single');
  const [showClassList, setShowClassList] = useState(false);

  const toggleClass = (classId: number) => {
    setFormData(prev => ({ ...prev, selected_class_ids: prev.selected_class_ids.includes(classId) ? prev.selected_class_ids.filter(id => id !== classId) : [...prev.selected_class_ids, classId] }));
  };

  const toggleSubject = (subjectId: number) => {
    setFormData(prev => ({ ...prev, selected_subject_ids: prev.selected_subject_ids.includes(subjectId) ? prev.selected_subject_ids.filter(id => id !== subjectId) : [...prev.selected_subject_ids, subjectId] }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.selected_class_ids.length === 0) { toast.error("Veuillez sélectionner au moins une classe"); return; }
    if (formData.selected_subject_ids.length === 0) { toast.error("Veuillez sélectionner au moins une matière"); return; }
    if (!formData.teacher_id) { toast.error("Veuillez sélectionner un enseignant"); return; }
    setSaving(true);

    let successCount = 0;
    let skipCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      if (assignment?.id) {
        await apiService.put(`/timetable/assignments/${assignment.id}`, {
          teacher_id: parseInt(formData.teacher_id), subject_id: formData.selected_subject_ids[0],
          class_id: formData.selected_class_ids[0],
          is_principal_teacher: formData.is_principal, academic_year_id: academicYearId, school_id: schoolId,
        });
        toast.success("Affectation mise à jour");
      } else {
        for (const subjectId of formData.selected_subject_ids) {
          for (const classId of formData.selected_class_ids) {
            try {
              await apiService.post('/timetable/assignments', {
                teacher_id: parseInt(formData.teacher_id), subject_id: subjectId, class_id: classId,
                is_principal_teacher: formData.is_principal, academic_year_id: academicYearId, school_id: schoolId,
              });
              successCount++;
            } catch (err: any) {
              if (err?.response?.status === 409) {
                skipCount++;
              } else {
                errorCount++;
                const msg = err?.response?.data?.detail || err?.message || 'Erreur';
                if (!errors.includes(msg)) errors.push(msg);
              }
            }
          }
        }

        if (successCount > 0) toast.success(`${successCount} affectation${successCount > 1 ? 's' : ''} créée${successCount > 1 ? 's' : ''}`);
        if (skipCount > 0) toast.info(`${skipCount} doublon${skipCount > 1 ? 's' : ''} ignoré${skipCount > 1 ? 's' : ''}`);
        if (errorCount > 0) toast.error(`${errorCount} erreur${errorCount > 1 ? 's' : ''}: ${errors[0]}`);
      }
      onSuccess();
    } catch (err: any) { 
      toast.error(err?.response?.data?.detail || "Erreur lors de l'enregistrement"); 
    }
    finally { setSaving(false); }
  };

  const selectedClasses = classes.filter(c => formData.selected_class_ids.includes(c.id));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">{assignment?.id ? "Modifier l'Affectation" : "Nouvelle Affectation"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enseignant *</label>
              <SearchableSelect
                required
                value={formData.teacher_id}
                onChange={(val) => setFormData({ ...formData, teacher_id: val })}
                placeholder="Rechercher et sélectionner un enseignant..."
                options={teachers.map((t) => ({ id: t.id, label: t.full_name || `${t.first_name} ${t.last_name}` }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matière(s) * <span className="text-xs font-normal text-gray-500">({formData.selected_subject_ids.length} sélectionnée{formData.selected_subject_ids.length > 1 ? 's' : ''})</span>
              </label>
              <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto space-y-1 bg-white">
                {subjects.length === 0 ? <p className="text-sm text-gray-400">Aucune matière</p> : subjects.map((s) => (
                  <label key={s.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${formData.selected_subject_ids.includes(s.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                    <input type="checkbox" checked={formData.selected_subject_ids.includes(s.id)} onChange={() => toggleSubject(s.id)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                    <span className="text-sm text-gray-700">{s.name} {s.code ? `(${s.code})` : ''}</span>
                  </label>
                ))}
              </div>
              {formData.selected_subject_ids.length === 0 && <p className="text-xs text-red-500 mt-1">Veuillez sélectionner au moins une matière</p>}
            </div>
            <div className="flex items-center gap-4 bg-gray-50 p-3 rounded-lg">
              <span className="text-sm font-medium text-gray-700">Mode:</span>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="saveMode" checked={saveMode === 'single'} onChange={() => { setSaveMode('single'); setFormData(prev => ({ ...prev, selected_class_ids: [] })); }} className="w-4 h-4 text-[#1e3a8a]" />
                Simple (1 classe)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name="saveMode" checked={saveMode === 'batch'} onChange={() => setSaveMode('batch')} className="w-4 h-4 text-[#1e3a8a]" />
                Multiple (plusieurs classes)
              </label>
            </div>
            {saveMode === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Classe *</label>
                <select required value={formData.selected_class_ids[0] || ""} onChange={(e) => setFormData({ ...formData, selected_class_ids: e.target.value ? [parseInt(e.target.value)] : [] })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{classes.length === 0 ? 'Chargement...' : 'Sélectionner...'}</option>
                  {classes.length > 0 ? classes.map((c) => <option key={c.id} value={c.id}>{c.name} {c.level_name ? `(${c.level_name})` : ''}</option>) : <option value="" disabled>Aucune classe disponible</option>}
                </select>
                {classes.length === 0 && <p className="text-xs text-red-500 mt-1">Aucune classe chargée</p>}
              </div>
            )}
            {saveMode === 'batch' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Classes * <span className="text-xs font-normal text-gray-500">({formData.selected_class_ids.length} sélectionnée{formData.selected_class_ids.length > 1 ? 's' : ''})</span></label>
                <button type="button" onClick={() => setShowClassList(!showClassList)} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  <span className="text-gray-700">{showClassList ? 'Masquer la liste' : 'Afficher la liste des classes'}</span>
                  <span className="text-gray-500">{showClassList ? '▲' : '▼'}</span>
                </button>
                {showClassList && (
                  <div className="border border-gray-300 rounded-lg p-3 mt-2 max-h-48 overflow-y-auto bg-white">
                    {classes.length === 0 ? <p className="text-sm text-gray-500 text-center py-4">Chargement...</p> : (
                      <div className="space-y-1">
                        {classes.map((c) => (
                          <label key={c.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${formData.selected_class_ids.includes(c.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'}`}>
                            <input type="checkbox" checked={formData.selected_class_ids.includes(c.id)} onChange={() => toggleClass(c.id)} className="w-4 h-4 text-[#1e3a8a] rounded" />
                            <div className="flex-1"><span className="text-sm font-medium text-gray-900">{c.name}</span>{c.level_name && <span className="text-xs text-gray-500 ml-2">({c.level_name})</span>}</div>
                            {formData.selected_class_ids.includes(c.id) && <CheckCircle className="w-4 h-4 text-blue-600" />}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {selectedClasses.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedClasses.map(c => <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">{c.name}<button type="button" onClick={() => toggleClass(c.id)} className="hover:text-blue-600"><X className="w-3 h-3" /></button></span>)}
                  </div>
                )}
                {formData.selected_class_ids.length === 0 && <p className="text-xs text-red-500 mt-1">Veuillez sélectionner au moins une classe</p>}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={formData.is_principal} onChange={(e) => setFormData({ ...formData, is_principal: e.target.checked })} className="w-4 h-4 rounded" />
              Enseignant principal de la classe
            </label>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3 flex-shrink-0">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">Annuler</button>
            <button type="submit" disabled={saving || formData.selected_class_ids.length === 0} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center gap-2">
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {assignment?.id ? "Mettre à jour" : saveMode === 'batch' && formData.selected_class_ids.length > 1 ? `Créer ${formData.selected_class_ids.length} affectations` : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ==================== DELETE CONFIRM MODAL ====================
function DeleteConfirmModal({ title, message, onConfirm, onCancel }: { title: string; message: string; onConfirm: () => void; onCancel: () => void; }) {
  const [deleting, setDeleting] = useState(false);
  const handleConfirm = async () => { setDeleting(true); await onConfirm(); setDeleting(false); };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-in fade-in zoom-in duration-200">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0"><TriangleAlert className="w-6 h-6 text-red-600" /></div>
            <div><h3 className="text-lg font-semibold text-gray-900">{title}</h3></div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed ml-[60px]">{message}</p>
        </div>
        <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3">
          <button onClick={onCancel} disabled={deleting} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50">Annuler</button>
          <button onClick={handleConfirm} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2">
            {deleting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {deleting ? "Suppression..." : "Supprimer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== AUTO GENERATE TAB ====================
function AutoGenerateTab({ classes, subjects, teachers, academicYearId, schoolId, onSuccess, canWrite }: {
  classes: ClassOption[]; subjects: Subject[]; teachers: Teacher[];
  academicYearId: number; schoolId: number; onSuccess: () => void; canWrite: boolean;
}) {
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [startTime, setStartTime] = useState("07:30");
  const [endTime, setEndTime] = useState("16:30");
  const [breakCount, setBreakCount] = useState(2);
  const [breakDuration, setBreakDuration] = useState(15);
  const [slotDuration, setSlotDuration] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 4, 5]);

  const [constraints, setConstraints] = useState([
    { id: 'eps_before_break2', label: 'EPS avant la 2eme pause', description: 'EPS ne doit pas depasser la 2eme pause', enabled: true },
    { id: 'class_end_time', label: 'Classes de 6eme terminent a 14h', description: 'Les classes de 6eme terminent a 14h00', enabled: true },
    { id: 'teacher_single_room', label: 'Un enseignant = une salle a la fois', description: 'Un enseignant ne doit pas etre dans deux salles au meme moment', enabled: true },
    { id: 'teacher_max_days', label: 'Enseignant: max 3 jours de cours', description: 'Un enseignant a droit a 3 jours de cours (2 repos)', enabled: true },
    { id: 'teacher_no_duplicate_class', label: 'Pas 2x meme classe par jour', description: 'Un enseignant ne passe pas deux fois dans une classe le meme jour', enabled: true },
  ]);
  const [newConstraintLabel, setNewConstraintLabel] = useState("");
  const [newConstraintDesc, setNewConstraintDesc] = useState("");
  const [showAddConstraint, setShowAddConstraint] = useState(false);

  const [slotDurations, setSlotDurations] = useState<number[]>([30, 45, 60, 90, 120]);
  const [newDuration, setNewDuration] = useState("");
  const [showAddDuration, setShowAddDuration] = useState(false);

  // ─── PREVIEW STATE ───
  const [showPreview, setShowPreview] = useState(false);
  const [previewResults, setPreviewResults] = useState<any[]>([]);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewErrors, setPreviewErrors] = useState<string[] | null>(null);
  const [savingPreview, setSavingPreview] = useState(false);

  const settingsStorageKey = `timetable-gen-settings-${schoolId}-${academicYearId}`;

  const saveSettingsToStorage = useCallback((data: any) => {
    try { localStorage.setItem(settingsStorageKey, JSON.stringify(data)); } catch (e) { console.warn('localStorage save failed', e); }
  }, [settingsStorageKey]);

  const loadSettingsFromStorage = useCallback(() => {
    try { const raw = localStorage.getItem(settingsStorageKey); if (raw) return JSON.parse(raw); } catch (e) { console.warn('localStorage load failed', e); }
    return null;
  }, [settingsStorageKey]);

  // ─── Chargement initial ───
  useEffect(() => {
    const stored = loadSettingsFromStorage();
    if (stored) {
      if (stored.start_time) setStartTime(stored.start_time);
      if (stored.end_time) setEndTime(stored.end_time);
      if (typeof stored.break_count === 'number') setBreakCount(stored.break_count);
      if (typeof stored.break_duration_minutes === 'number') setBreakDuration(stored.break_duration_minutes);
      if (typeof stored.slot_duration_minutes === 'number') setSlotDuration(stored.slot_duration_minutes);
      if (stored.days && Array.isArray(stored.days)) setSelectedDays(stored.days);
      if (stored.class_ids && Array.isArray(stored.class_ids)) setSelectedClasses(stored.class_ids);
      if (stored.constraints && Array.isArray(stored.constraints)) {
        setConstraints(prev => {
          const merged = [...prev];
          stored.constraints.forEach((c: any) => {
            const idx = merged.findIndex(x => x.id === c.type);
            if (idx >= 0) {
              merged[idx].enabled = c.enabled !== false;
              if (c.label) merged[idx].label = c.label;
              if (c.description) merged[idx].description = c.description;
            } else if (c.type) {
              merged.push({
                id: c.type,
                label: c.label || c.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                description: c.description || c.type.replace(/_/g, ' '),
                enabled: c.enabled !== false
              });
            }
          });
          return merged;
        });
      }
      if (stored.slot_durations && Array.isArray(stored.slot_durations)) {
        setSlotDurations(prev => [...new Set([...prev, ...stored.slot_durations])].sort((a, b) => a - b));
      }
    }

    const loadFromBackend = async () => {
      try {
        const res: any = await apiService.get('/timetable/generation-settings', {
          params: { school_id: schoolId, academic_year_id: academicYearId }
        });
        if (res && res.start_time) {
          setStartTime(res.start_time);
          setEndTime(res.end_time);
          setBreakCount(res.break_count);
          setBreakDuration(res.break_duration_minutes);
          setSlotDuration(res.slot_duration_minutes);
          if (res.days && Array.isArray(res.days)) setSelectedDays(res.days);
          if (res.class_ids && Array.isArray(res.class_ids)) setSelectedClasses(res.class_ids);
          if (res.constraints && Array.isArray(res.constraints)) {
            setConstraints(prev => {
              const merged = [...prev];
              res.constraints.forEach((c: any) => {
                const idx = merged.findIndex(x => x.id === c.type);
                if (idx >= 0) {
                  merged[idx].enabled = c.enabled !== false;
                  if (c.label) merged[idx].label = c.label;
                  if (c.description) merged[idx].description = c.description;
                } else if (c.type) {
                  merged.push({
                    id: c.type,
                    label: c.label || c.type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()),
                    description: c.description || c.type.replace(/_/g, ' '),
                    enabled: c.enabled !== false
                  });
                }
              });
              return merged;
            });
          }
          if (res.slot_durations && Array.isArray(res.slot_durations)) {
            setSlotDurations(prev => [...new Set([...prev, ...res.slot_durations])].sort((a, b) => a - b));
          }
          saveSettingsToStorage(res);
        }
      } catch (e) {
        console.log('[AutoGenerate] Backend settings unavailable, using localStorage');
      }
    };
    loadFromBackend();
  }, [schoolId, academicYearId, loadSettingsFromStorage, saveSettingsToStorage]);

  // ─── Persistence auto des contraintes ───
  const saveConstraints = useCallback(async () => {
    const payload = {
      school_id: schoolId, academic_year_id: academicYearId,
      start_time: startTime, end_time: endTime,
      break_count: breakCount, break_duration_minutes: breakDuration,
      slot_duration_minutes: slotDuration, days: selectedDays,
      class_ids: selectedClasses,
      constraints: constraints.map(c => ({
        type: c.id, label: c.label, description: c.description, enabled: c.enabled
      })),
      slot_durations: slotDurations,
    };
    saveSettingsToStorage(payload);
    try {
      await apiService.post('/timetable/generation-settings', payload);
    } catch (e) {
      console.warn('[AutoGenerate] Backend save failed, kept in localStorage');
    }
  }, [schoolId, academicYearId, startTime, endTime, breakCount, breakDuration, slotDuration, selectedDays, selectedClasses, constraints, slotDurations, saveSettingsToStorage]);

  // Debounce: sauvegarde auto 1.5s après modification des contraintes
  useEffect(() => {
    const timer = setTimeout(() => {
      saveConstraints();
    }, 1500);
    return () => clearTimeout(timer);
  }, [constraints, saveConstraints]);

  const DAYS_LABELS = ["", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

  const toggleDay = (day: number) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a,b) => a-b));
  };

  const toggleClass = (classId: number) => {
    setSelectedClasses(prev => prev.includes(classId) ? prev.filter(c => c !== classId) : [...prev, classId].sort((a,b) => a-b));
  };

  const selectAllClasses = () => { setSelectedClasses(classes.map(c => c.id)); };
  const deselectAllClasses = () => { setSelectedClasses([]); };

  const toggleConstraint = (id: string) => {
    setConstraints(prev => prev.map(c => c.id === id ? { ...c, enabled: !c.enabled } : c));
  };

  const addConstraint = () => {
    if (!newConstraintLabel.trim()) return;
    const id = 'custom_' + Date.now();
    setConstraints(prev => [...prev, { id, label: newConstraintLabel, description: newConstraintDesc || newConstraintLabel, enabled: true }]);
    setNewConstraintLabel(""); setNewConstraintDesc(""); setShowAddConstraint(false);
  };

  const removeConstraint = (id: string) => { setConstraints(prev => prev.filter(c => c.id !== id)); };

  const addDuration = () => {
    const dur = parseInt(newDuration);
    if (!dur || dur < 5 || slotDurations.includes(dur)) return;
    setSlotDurations(prev => [...prev, dur].sort((a, b) => a - b));
    setNewDuration(""); setShowAddDuration(false);
  };

  const removeDuration = (dur: number) => {
    if (slotDurations.length <= 1) { toast.error("Au moins une duree de creneau est requise"); return; }
    setSlotDurations(prev => prev.filter(d => d !== dur));
  };

  // ─── GÉNÉRATION APERÇU ───
  const handleGeneratePreview = async () => {
    if (selectedClasses.length === 0) { toast.error("Veuillez selectionner au moins une classe"); return; }
    if (selectedDays.length === 0) { toast.error("Veuillez selectionner au moins un jour"); return; }
    setGenerating(true);

    const settingsPayload = {
      school_id: schoolId, academic_year_id: academicYearId,
      start_time: startTime, end_time: endTime,
      break_count: breakCount, break_duration_minutes: breakDuration,
      slot_duration_minutes: slotDuration, days: selectedDays,
      class_ids: selectedClasses,
      constraints: constraints.map(c => ({
        type: c.id, label: c.label, description: c.description, enabled: c.enabled
      })),
      slot_durations: slotDurations,
    };

    saveSettingsToStorage(settingsPayload);

    try {
      const result: any = await apiService.post('/timetable/generate-auto-preview', {
        class_ids: selectedClasses, academic_year_id: academicYearId, school_id: schoolId,
        start_time: startTime, end_time: endTime,
        break_count: breakCount, break_duration_minutes: breakDuration,
        slot_duration_minutes: slotDuration, days: selectedDays,
        constraints: constraints.filter(c => c.enabled).map(c => ({ type: c.id, enabled: true })),
      });

      const results = result?.results || [];
      setPreviewResults(results);
      setPreviewMessage(result?.message || `${result?.total_slots || 0} creneaux proposes`);
      setPreviewErrors(result?.errors || null);
      setShowPreview(true);
      toast.success("Aperçu genere — verifiez la proposition avant de valider");
    } catch (err: any) {
      console.error('Auto generate preview error:', err);
      toast.error(err?.response?.data?.detail || "Erreur lors de la generation de l'apercu");
    } finally { setGenerating(false); }
  };

  // ─── VALIDATION (vrai save) ───
  const handleConfirmSave = async () => {
    if (selectedClasses.length === 0) return;
    setSavingPreview(true);
    try {
      const result: any = await apiService.post('/timetable/generate-auto', {
        class_ids: selectedClasses, academic_year_id: academicYearId, school_id: schoolId,
        start_time: startTime, end_time: endTime,
        break_count: breakCount, break_duration_minutes: breakDuration,
        slot_duration_minutes: slotDuration, days: selectedDays,
        constraints: constraints.filter(c => c.enabled).map(c => ({ type: c.id, enabled: true })),
      });
      toast.success(result?.message || "Emploi du temps sauvegarde avec succes");
      setShowPreview(false);
      setPreviewResults([]);
      onSuccess();
    } catch (err: any) {
      console.error('Auto generate save error:', err);
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally { setSavingPreview(false); }
  };

  // ─── Supprimer un creneau de l'apercu ───
  const removePreviewSlot = (classId: number, slotIndex: number) => {
    setPreviewResults(prev => prev.map(r => {
      if (r.class_id !== classId) return r;
      return { ...r, slots: r.slots.filter((_: any, i: number) => i !== slotIndex), slots_generated: r.slots.length - 1 };
    }));
  };

  const slotsPerDay = Math.max(0, Math.floor(
    ((parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1])) 
     - (parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1])) 
     - breakCount * breakDuration) / slotDuration
  ));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Grid3X3 className="w-5 h-5 text-[#1e3a8a]" />
          Generation Automatique de l'Emploi du Temps
        </h2>
        <p className="text-sm text-gray-500 mb-6">Cette fonction genere automatiquement un emploi du temps a partir des affectations enseignant-matiere-classe. Un apercu vous sera propose avant validation.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">Parametres de base</h3>

            {/* ─── SELECTION MULTI-CLASSES ─── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Classes *</label>
                <div className="flex gap-1">
                  <button onClick={selectAllClasses} className="text-xs text-[#1e3a8a] hover:underline font-medium">Tout</button>
                  <span className="text-xs text-gray-400">|</span>
                  <button onClick={deselectAllClasses} className="text-xs text-gray-500 hover:underline font-medium">Aucune</button>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {classes.map((c) => (
                  <label key={c.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selectedClasses.includes(c.id) ? 'bg-blue-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={selectedClasses.includes(c.id)}
                      onChange={() => toggleClass(c.id)}
                      className="w-4 h-4 text-[#1e3a8a] rounded border-gray-300 focus:ring-[#1e3a8a]"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.level_name || ''} {c.abbreviation || ''}</p>
                    </div>
                    {selectedClasses.includes(c.id) && <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                  </label>
                ))}
                {classes.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucune classe chargee</p>}
              </div>
              <p className="text-xs text-gray-500 mt-1">{selectedClasses.length} classe(s) selectionnee(s)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Jours de la semaine *</label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((day) => (
                  <button key={day} onClick={() => toggleDay(day)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedDays.includes(day) ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {DAYS_LABELS[day]}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Heure debut *</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Heure fin *</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Pauses</label><input type="number" min={0} max={3} value={breakCount} onChange={(e) => setBreakCount(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1.5">Duree pause (min)</label><input type="number" min={5} max={60} step={5} value={breakDuration} onChange={(e) => setBreakDuration(parseInt(e.target.value) || 15)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a]" /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Duree creneau</label>
                <button type="button" onClick={() => setShowAddDuration(!showAddDuration)} className="text-xs text-[#1e3a8a] hover:underline font-medium">{showAddDuration ? 'Annuler' : '+ Ajouter'}</button>
              </div>
              <select value={slotDuration} onChange={(e) => setSlotDuration(parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1e3a8a] mb-2">
                {slotDurations.map((dur) => (<option key={dur} value={dur}>{dur < 60 ? `${dur} minutes` : dur === 60 ? '1 heure' : `${Math.floor(dur/60)}h${dur % 60 > 0 ? dur % 60 : ''}`}</option>))}
              </select>
              {showAddDuration && (
                <div className="flex gap-2">
                  <input type="number" min={5} step={5} placeholder="Duree (min)" value={newDuration} onChange={(e) => setNewDuration(e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button onClick={addDuration} className="px-3 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e]">OK</button>
                </div>
              )}
              <div className="flex flex-wrap gap-1 mt-2">
                {slotDurations.map((dur) => (
                  <span key={dur} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{dur}min<button onClick={() => removeDuration(dur)} className="text-gray-400 hover:text-red-500">x</button></span>
                ))}
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
              <p className="font-medium mb-1">Resume:</p>
              <p>{startTime} — {endTime} | {breakCount} pause(s) de {breakDuration}min</p>
              <p className="font-semibold">{slotsPerDay} creneaux/jour x {selectedDays.length} jours = {slotsPerDay * selectedDays.length} total</p>
              <p className="font-semibold mt-1">{selectedClasses.length} classe(s) selectionnee(s)</p>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><TriangleAlert className="w-4 h-4 text-amber-600" />Contraintes de generation</h3>
              <button onClick={() => setShowAddConstraint(!showAddConstraint)} className="text-xs text-[#1e3a8a] hover:underline font-medium">{showAddConstraint ? 'Annuler' : '+ Ajouter une contrainte'}</button>
            </div>
            {showAddConstraint && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-700">Nouvelle contrainte</p>
                <input type="text" placeholder="Nom de la contrainte" value={newConstraintLabel} onChange={(e) => setNewConstraintLabel(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="text" placeholder="Description (optionnelle)" value={newConstraintDesc} onChange={(e) => setNewConstraintDesc(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => { setShowAddConstraint(false); setNewConstraintLabel(""); setNewConstraintDesc(""); }} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Annuler</button>
                  <button onClick={addConstraint} disabled={!newConstraintLabel.trim()} className="px-3 py-1.5 text-sm bg-[#1e3a8a] text-white rounded-lg hover:bg-[#152a5e] disabled:opacity-50">Ajouter</button>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500">Activez, desactivez ou supprimez les contraintes selon vos besoins. Elles sont sauvegardees automatiquement.</p>
            <div className="space-y-3">
              {constraints.map((c) => (
                <div key={c.id} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${c.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                  <button onClick={() => toggleConstraint(c.id)} className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center transition-colors ${c.enabled ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>{c.enabled && <CheckCircle className="w-4 h-4" />}</button>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${c.enabled ? 'text-green-800' : 'text-gray-600'}`}>{c.label}</p>
                    <p className="text-xs text-gray-500">{c.description}</p>
                  </div>
                  <button onClick={() => removeConstraint(c.id)} className="p-1 text-gray-400 hover:text-red-500 transition-colors" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
              {constraints.length === 0 && <p className="text-sm text-gray-400 italic text-center py-4">Aucune contrainte definie</p>}
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
          <button onClick={handleGeneratePreview} disabled={generating || !canWrite || selectedClasses.length === 0} className="px-6 py-2.5 bg-gradient-to-r from-[#1e3a8a] to-blue-600 text-white rounded-lg text-sm font-medium hover:from-[#152a5e] hover:to-blue-700 transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            {generating ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Generation en cours...</> : <><Grid3X3 className="w-4 h-4" />Generer l'apercu ({selectedClasses.length} classe{selectedClasses.length > 1 ? 's' : ''})</>}
          </button>
        </div>
      </div>

      {/* ─── MODAL APERÇU ─── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">📋 Apercu de l'Emploi du Temps Genere</h2>
                <p className="text-xs text-gray-500">{previewMessage}</p>
              </div>
              <button onClick={() => { setShowPreview(false); setPreviewResults([]); setPreviewErrors(null); }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {previewErrors && previewErrors.length > 0 && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800">Erreurs rencontrees:</p>
                  <ul className="mt-1 space-y-1">
                    {previewErrors.map((err, i) => (
                      <li key={i} className="text-xs text-red-700">• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
              {previewResults.length === 0 ? (
                <div className="text-center py-12 text-gray-500">Aucun creneau propose.</div>
              ) : (
                <div className="space-y-6">
                  <p className="text-sm text-gray-600">Verifiez la proposition ci-dessous. Vous pouvez supprimer des creneaux indesirables avant de valider.</p>
                  {previewResults.map((result: any) => (
                    <div key={result.class_id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-100 px-4 py-2 flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-800">{result.class_name || `Classe #${result.class_id}`}</span>
                        <span className="text-xs text-gray-500">{result.slots_generated || 0} creneau(x)</span>
                      </div>
                      {result.slots && result.slots.length > 0 ? (
                        <div className="divide-y divide-gray-100">
                          {selectedDays.map(day => {
                            const daySlots = result.slots.filter((s: any) => s.day === day).sort((a: any, b: any) => a.start_time.localeCompare(b.start_time));
                            if (daySlots.length === 0) return null;
                            return (
                              <div key={day}>
                                <div className="bg-gray-50 px-4 py-1.5 text-xs font-semibold text-gray-600">{DAYS_LABELS[day]}</div>
                                {daySlots.map((slot: any, idx: number) => {
                                  const subject = subjects.find(s => s.id === slot.subject_id);
                                  const teacher = teachers.find(t => t.id === slot.teacher_id);
                                  const globalIdx = result.slots.indexOf(slot);
                                  return (
                                    <div key={idx} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50">
                                      <div className="flex items-center gap-3">
                                        <div className="w-16 text-xs font-medium text-gray-600">{slot.start_time}</div>
                                        <div className="w-16 text-xs text-gray-500">{slot.end_time}</div>
                                        <div className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">{subject?.name || `Matiere #${slot.subject_id}`}</div>
                                        <div className="text-xs text-gray-600">{teacher?.full_name || `Prof #${slot.teacher_id}`}</div>
                                      </div>
                                      <button onClick={() => removePreviewSlot(result.class_id, globalIdx)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Retirer de l'apercu">
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-400 italic">Aucun creneau pour cette classe</div>
                      )}
                    </div>
                  ))}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <p className="font-medium">⚠️ Avant de valider</p>
                    <p className="text-xs mt-1">Les creneaux supprimes ci-dessus ne seront pas crees. Cliquez sur « Valider et sauvegarder » pour persister l'emploi du temps definitif. Vous pourrez toujours le modifier ensuite dans l'onglet Emploi du Temps.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-between flex-shrink-0">
              <button onClick={() => { setShowPreview(false); setPreviewResults([]); setPreviewErrors(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
                Annuler
              </button>
              <div className="flex items-center gap-2">
                <button onClick={handleGeneratePreview} disabled={generating} className="px-4 py-2 border border-[#1e3a8a] text-[#1e3a8a] rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors disabled:opacity-50">
                  {generating ? <><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Regenerer...</> : '↻ Regenerer'}
                </button>
                <button onClick={handleConfirmSave} disabled={savingPreview || previewResults.filter((r: any) => r.slots?.length > 0).length === 0} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center gap-2 transition-colors">
                  {savingPreview && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  Valider et sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}